// src/components/roads/roadCompletedWorkIntel.js
// --- UPDATE (Road Works Report — Completed work) -----------------------------
// FlowSense explainable sign-off helpers for completed road work (pure functions, no React).
//
//   evidenceScore(job)                  0–100 evidence score for one completed job + every reason
//   analyzeCompletedWork(data)          headline, findings (evidence gaps), stats
//   explainJobLocally(job)              plain-language "what was done" summary (rules)
//   answerCompletedWorkLocally(q, d)    rules answers when FlowSense AI (Ollama) is offline
//   buildCompletedWorkAiContext(d, job) compact numbers-only context for FlowSense AI
//   filterTimeline(data, filters)       jobs + inspections merged into one dated timeline
//   timelineToCsv(rows)                 CSV for the "Export CSV" button
//
// Nothing is invented: a value the server did not send stays null and the UI shows "—".
// --- END UPDATE ---------------------------------------------------------------

const num = (v, d = null) => {
    if (v === null || v === undefined || v === '') return d;
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
};
const lc = (v) => String(v ?? '').toLowerCase();
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

export const GAP_LABEL = {
    no_after_photo: 'No "after" photo',
    no_before_photo: 'No "before" photo',
    no_checklist: 'Checklist not submitted',
    no_qa: 'No QA audit',
    no_notes: 'No resolution notes',
    no_completed_at: 'Completion time not recorded',
};

/**
 * Evidence score for one completed job — how well the sign-off is supported by field evidence.
 * Every point has a reason so supervisors can see exactly why a job scores what it scores.
 */
export function evidenceScore(job) {
    if (!job) return { score: null, reasons: [] };
    const reasons = [];
    let score = 100;
    const ded = (pts, why) => { if (pts > 0) { score -= pts; reasons.push({ points: -pts, why }); } };
    const codes = new Set((job.gaps || []).map((g) => g.code));
    ded(codes.has('no_after_photo') ? 30 : 0, 'no completion ("after") photo');
    ded(codes.has('no_before_photo') ? 15 : 0, 'no "before" photo of the defect');
    ded(codes.has('no_checklist') ? 20 : 0, 'acceptance checklist not submitted');
    ded(codes.has('no_qa') ? 10 : 0, 'paving job without a QA audit');
    ded(codes.has('no_notes') ? 15 : 0, 'no resolution notes');
    ded(codes.has('no_completed_at') ? 5 : 0, 'completion time not recorded');
    const ck = job.checklist;
    if (ck && num(ck.score) !== null && num(ck.score) < 55) ded(10, `checklist scored ${ck.score} (${ck.rating || 'low'})`);
    const qa = (job.qa || [])[0];
    if (qa?.ai_approval_status === 'rework_mandated') ded(20, 'QA audit mandated rework');
    else if (qa?.ai_approval_status === 'flagged_for_penalty') ded(8, 'QA audit flagged for penalty');
    if (job.asphalt?.cold) ded(5, `${plural(job.asphalt.cold, 'asphalt load')} arrived cold`);
    score = Math.max(0, Math.min(100, score));
    const level = score >= 85 ? { label: 'Well evidenced', color: 'green' } : score >= 60 ? { label: 'Partly evidenced', color: 'yellow' } : { label: 'Weak evidence', color: 'red' };
    return { score, level, reasons };
}

/** Panel-level analysis: headline + ranked findings. */
export function analyzeCompletedWork(data) {
    const jobs = data?.jobs || [];
    const inspections = data?.inspections || [];
    const s = data?.summary || {};
    const findings = [];
    const add = (f) => findings.push(f);
    const noAfter = jobs.filter((j) => (j.gaps || []).some((g) => g.code === 'no_after_photo'));
    const noCk = jobs.filter((j) => (j.gaps || []).some((g) => g.code === 'no_checklist'));
    const noQa = jobs.filter((j) => (j.gaps || []).some((g) => g.code === 'no_qa'));
    const noNotes = jobs.filter((j) => (j.gaps || []).some((g) => g.code === 'no_notes'));
    const rework = jobs.filter((j) => (j.qa || [])[0]?.ai_approval_status === 'rework_mandated');
    const lowInsp = inspections.filter((i) => num(i.score) !== null && num(i.score) < 55);
    if (rework.length) add({ id: 'rework', level: 'critical', title: `${plural(rework.length, 'completed job')} failed QA (rework mandated)`, detail: rework.slice(0, 3).map((j) => j.title).join(' · '), jobId: rework[0].id });
    if (noAfter.length) add({ id: 'no_after', level: 'high', title: `${plural(noAfter.length, 'job')} closed without an "after" photo`, detail: 'There is no photo proof of the finished work.', jobId: noAfter[0].id });
    if (noCk.length) add({ id: 'no_checklist', level: 'high', title: `${plural(noCk.length, 'acceptance job')} without a submitted checklist`, detail: noCk.slice(0, 3).map((j) => j.title).join(' · '), jobId: noCk[0].id });
    if (noQa.length) add({ id: 'no_qa', level: 'medium', title: `${plural(noQa.length, 'paving job')} without a QA audit`, detail: 'Run the AI workmanship audit before final sign-off.', jobId: noQa[0].id });
    if (noNotes.length) add({ id: 'no_notes', level: 'medium', title: `${plural(noNotes.length, 'job')} without resolution notes`, detail: 'The record does not say what was done.', jobId: noNotes[0].id });
    if (lowInsp.length) add({ id: 'low_insp', level: 'medium', title: `${plural(lowInsp.length, 'inspection')} scored below 55`, detail: lowInsp.slice(0, 3).map((i) => `${i.template_label || i.template_key} ${i.score}`).join(' · '), checklistId: lowInsp[0].id });
    const order = { critical: 0, high: 1, medium: 2, info: 3 };
    findings.sort((a, b) => order[a.level] - order[b.level]);

    const scores = jobs.map((j) => evidenceScore(j).score).filter((v) => v !== null);
    const avgEvidence = scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
    const headline = !jobs.length && !inspections.length
        ? 'No completed jobs or submitted inspections on this road yet.'
        : findings.length
            ? `${plural(jobs.length, 'completed job')} and ${plural(inspections.length, 'inspection')}. Top issue: ${findings[0].title}.`
            : `${plural(jobs.length, 'completed job')} and ${plural(inspections.length, 'inspection')} — every job has its photos and paperwork.`;
    return {
        headline,
        findings,
        stats: {
            jobs: jobs.length,
            inspections: inspections.length,
            photos: num(s.photos, 0),
            avgEvidence,
            avgHours: num(s.avg_hours_to_complete),
            onTimePct: num(s.on_time_pct),
            noAfter: noAfter.length, noChecklist: noCk.length, noQa: noQa.length, noNotes: noNotes.length, rework: rework.length,
            inspectionAvg: num(s.inspections_avg_score),
            defects: num(s.inspection_defects, 0),
            partsCost: num(s.parts_cost),
            asphaltTons: num(s.asphalt_tons),
        },
    };
}

const fmtHours = (h) => {
    if (h === null || h === undefined) return null;
    if (h < 24) return `${h} h`;
    return `${Math.round((h / 24) * 10) / 10} days`;
};
export const formatDuration = (h) => fmtHours(num(h)) || '—';

/** Plain-language summary of one completed job (rules). */
export function explainJobLocally(job) {
    if (!job) return '';
    const ev = evidenceScore(job);
    const who = job.assigned_user?.name || job.assigned_user_username || 'an unassigned technician';
    const when = job.finished_at ? new Date(job.finished_at).toLocaleString() : 'an unknown date';
    const lines = [
        `${job.title} (${job.category || 'uncategorised'}) was completed by ${who} on ${when}${job.completed_at_recorded ? '' : ' (last update — the completion time was not recorded)'}.`,
        job.hours_open !== null ? `It was open for ${fmtHours(job.hours_open)}${job.hours_working !== null ? `, ${fmtHours(job.hours_working)} of it after work started` : ''}${job.on_time === true ? ' and finished on time.' : job.on_time === false ? ' and finished after the due date.' : '.'}` : null,
        job.resolution_notes ? `Technician notes: "${job.resolution_notes.slice(0, 300)}${job.resolution_notes.length > 300 ? '…' : ''}"` : 'There are no resolution notes.',
        `Photos: ${job.photo_counts?.before ?? 0} before, ${job.photo_counts?.during ?? 0} during, ${job.photo_counts?.after ?? 0} after, ${job.photo_counts?.inspection ?? 0} inspection.`,
        job.checklist ? `Acceptance checklist: ${job.checklist.score ?? '—'} (${job.checklist.rating || 'no rating'}).` : null,
        (job.qa || [])[0] ? `QA audit: ${job.qa[0].ai_quality_score ?? '—'} — ${String(job.qa[0].ai_approval_status || '').replace(/_/g, ' ')}.` : null,
        job.asphalt ? `Asphalt: ${job.asphalt.tons} t from ${plural(job.asphalt.trucks, 'truck')}${job.asphalt.cold ? `, ${job.asphalt.cold} arrived cold` : ''}.` : null,
        job.parts?.length ? `Parts: ${job.parts.map((p) => `${p.quantity} × ${p.name}`).join(', ')}.` : null,
        `Evidence score ${ev.score}/100 (${ev.level.label})${ev.reasons.length ? `: ${ev.reasons.map((r) => `${r.points} ${r.why}`).join('; ')}` : ''}.`,
    ];
    return lines.filter(Boolean).join('\n');
}

export function answerCompletedWorkLocally(question, data) {
    const q = lc(question);
    const a = analyzeCompletedWork(data);
    const s = a.stats;
    const res = (answer) => ({ answer, source: 'rules' });
    if (/photo|evidence|proof|picture/.test(q)) {
        return res(`${s.photos} field photo(s) on this road's completed work. ${s.noAfter ? `${plural(s.noAfter, 'job')} have no "after" photo.` : 'Every completed job has an "after" photo.'}${s.avgEvidence !== null ? ` Average evidence score ${s.avgEvidence}/100.` : ''}`);
    }
    if (/qa|audit|quality|rework/.test(q)) {
        return res(s.rework ? `${plural(s.rework, 'job')} failed QA and need rework.` : s.noQa ? `${plural(s.noQa, 'paving job')} have no QA audit yet.` : 'No QA problems on the completed jobs.');
    }
    if (/time|late|on time|how long|duration|sla/.test(q)) {
        return res(`${s.avgHours !== null ? `Jobs took ${fmtHours(s.avgHours)} on average from creation to completion.` : 'No completion durations are recorded.'} ${s.onTimePct !== null ? `${s.onTimePct}% finished by their due date.` : 'No due dates to compare against.'}`);
    }
    if (/inspect|checklist|survey/.test(q)) {
        return res(`${plural(s.inspections, 'submitted inspection')}${s.inspectionAvg !== null ? `, average score ${s.inspectionAvg}` : ''}, ${plural(s.defects, 'defect')} recorded.`);
    }
    return res([a.headline, ...a.findings.slice(0, 5).map((f) => `• [${f.level}] ${f.title}`)].join('\n'));
}

/** Compact numbers-only context for FlowSense AI. */
export function buildCompletedWorkAiContext(data, job = null) {
    const a = analyzeCompletedWork(data);
    const ctx = {
        page: 'Road Works Report — Completed work',
        road: data?.road || null,
        headline: a.headline,
        stats: a.stats,
        findings: a.findings.slice(0, 8).map((f) => ({ level: f.level, title: f.title, detail: f.detail })),
        recent_jobs: (data?.jobs || []).slice(0, 10).map((j) => ({
            id: j.id, title: j.title, category: j.category, finished_at: j.finished_at, hours_open: j.hours_open, on_time: j.on_time,
            photos: j.photo_counts, checklist_score: j.checklist?.score ?? null, qa: (j.qa || [])[0]?.ai_approval_status || null,
            gaps: (j.gaps || []).map((g) => g.code), evidence: evidenceScore(j).score,
        })),
        recent_inspections: (data?.inspections || []).slice(0, 8).map((i) => ({
            id: i.id, template: i.template_label, score: i.score, rating: i.rating, defects: i.counts?.failed ?? null, photos: i.photo_count,
        })),
    };
    if (job) {
        ctx.job = {
            id: job.id, title: job.title, category: job.category, type: job.work_order_type, priority: job.priority,
            technician: job.assigned_user?.name || null, created_at: job.created_at, started_at: job.started_at, finished_at: job.finished_at,
            hours_open: job.hours_open, hours_working: job.hours_working, on_time: job.on_time,
            resolution_notes: String(job.resolution_notes || '').slice(0, 1200),
            photos: job.photo_counts,
            photo_captions: Object.values(job.photos || {}).flat().map((p) => p.caption).filter(Boolean).slice(0, 12),
            checklists: (job.checklists || []).map((c) => ({ template: c.template_label, status: c.status, score: c.score, rating: c.rating, failed: c.failed_items.map((f) => ({ item: f.label, note: f.note, value: f.value, unit: f.unit })) })),
            qa: (job.qa || []).map((q) => ({ score: q.ai_quality_score, status: q.ai_approval_status, iri: q.iri, density_pct: q.density_pct, thermal: q.thermal_score })),
            asphalt: job.asphalt, parts: (job.parts || []).map((p) => ({ name: p.name, qty: p.quantity })),
            gaps: (job.gaps || []).map((g) => g.message), evidence: evidenceScore(job),
        };
    }
    return ctx;
}

/** Jobs + inspections merged into one timeline, newest first, filtered. */
export function filterTimeline(data, { kind = 'all', search = '', category = '', gapsOnly = false } = {}) {
    const q = lc(search).trim();
    const rows = [];
    if (kind !== 'inspections') {
        (data?.jobs || []).forEach((j) => rows.push({ kind: 'job', id: j.id, at: j.finished_at, job: j }));
    }
    if (kind !== 'jobs') {
        (data?.inspections || []).forEach((i) => {
            // an inspection that belongs to a completed job is shown inside that job, not twice
            if (i.linked_to_completed_job && kind === 'all') return;
            rows.push({ kind: 'inspection', id: i.id, at: i.submitted_at || i.updated_at, inspection: i });
        });
    }
    return rows
        .filter((r) => {
            if (category && r.kind === 'job' && (r.job.category || 'Uncategorised') !== category) return false;
            if (category && r.kind === 'inspection') return false;
            if (gapsOnly) {
                // "needs attention": any evidence gap, failed / flagged QA, low checklist, cold asphalt
                if (r.kind === 'job' && evidenceScore(r.job).score >= 85) return false;
                if (r.kind === 'inspection' && !(num(r.inspection.score) !== null && num(r.inspection.score) < 55)) return false;
            }
            if (!q) return true;
            const hay = r.kind === 'job'
                ? [r.job.id, r.job.title, r.job.category, r.job.resolution_notes, r.job.assigned_user?.name, r.job.description].join(' ')
                : [r.inspection.id, r.inspection.template_label, r.inspection.overall_notes, r.inspection.submitted_by?.name, ...(r.inspection.failed_items || []).map((f) => `${f.label} ${f.note}`)].join(' ');
            return lc(hay).includes(q);
        })
        .sort((a, b) => (new Date(b.at || 0).getTime() || 0) - (new Date(a.at || 0).getTime() || 0));
}

const csvCell = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function timelineToCsv(rows, roadName = '') {
    const head = ['Type', 'ID', 'Title / template', 'Category', 'Completed / submitted', 'Technician / inspector', 'Hours open', 'On time',
        'Before photos', 'After photos', 'Inspection photos', 'Checklist score', 'QA status', 'Evidence score', 'Gaps', 'Notes'];
    const body = rows.map((r) => {
        if (r.kind === 'job') {
            const j = r.job;
            return ['Job', j.id, j.title, j.category, j.finished_at, j.assigned_user?.name || j.assigned_user_username, j.hours_open,
                j.on_time === null ? '' : j.on_time ? 'yes' : 'no', j.photo_counts?.before, j.photo_counts?.after, j.photo_counts?.inspection,
                j.checklist?.score ?? '', (j.qa || [])[0]?.ai_approval_status || '', evidenceScore(j).score,
                (j.gaps || []).map((g) => GAP_LABEL[g.code] || g.code).join('; '), j.resolution_notes];
        }
        const i = r.inspection;
        return ['Inspection', i.id, i.template_label || i.template_key, '', i.submitted_at, i.submitted_by?.name || '', '', '', '', '', i.photo_count,
            i.score ?? '', '', '', '', i.overall_notes];
    });
    const title = roadName ? [[`Completed work — ${roadName}`], []] : [];
    return [...title, head, ...body].map((row) => row.map(csvCell).join(',')).join('\r\n');
}
